import { isPlatformBrowser } from "@angular/common";
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from "@angular/core";
import { interval, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-session-timeout-timer",
  templateUrl: "./session-timeout-timer.component.html",
  styleUrls: ["./session-timeout-timer.component.scss"]
})
export class SessionTimeoutTimerComponent implements OnInit, OnDestroy {
  @Input() sessionTimeout = 1200;
  @Input() warningThreshold = 300;
  @Input() criticalThreshold = 60;
  @Input() showProgressBar = true;
  @Input() compactMode = false;

  @Output() sessionExpired = new EventEmitter<void>();
  @Output() warningReached = new EventEmitter<number>();
  @Output() criticalReached = new EventEmitter<number>();

  timeRemaining: number;
  displayTime: string;
  progressPercentage: number;
  alertLevel: "normal" | "warning" | "critical" = "normal";

  private readonly destroyed$ = new Subject<void>();
  private warningEmitted = false;
  private criticalEmitted = false;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  ngOnInit(): void {
    this.timeRemaining = this.sessionTimeout;
    this.updateDisplay();

    // Only start the countdown timer in the browser
    if (isPlatformBrowser(this.platformId)) {
      this.startCountdown();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private startCountdown(): void {
    interval(1000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.timeRemaining--;
        this.updateDisplay();
        this.checkThresholds();

        if (this.timeRemaining <= 0) {
          this.handleSessionExpired();
        }
      });
  }

  private updateDisplay(): void {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;

    if (this.compactMode) {
      this.displayTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;
    } else {
      if (minutes > 0) {
        this.displayTime = `${minutes}m ${seconds}s`;
      } else {
        this.displayTime = `${seconds}s`;
      }
    }

    this.progressPercentage = (this.timeRemaining / this.sessionTimeout) * 100;

    if (this.timeRemaining <= this.criticalThreshold) {
      this.alertLevel = "critical";
    } else if (this.timeRemaining <= this.warningThreshold) {
      this.alertLevel = "warning";
    } else {
      this.alertLevel = "normal";
    }
  }

  private checkThresholds(): void {
    if (this.timeRemaining === this.warningThreshold && !this.warningEmitted) {
      this.warningEmitted = true;
      this.warningReached.emit(this.timeRemaining);
    }

    if (this.timeRemaining === this.criticalThreshold && !this.criticalEmitted) {
      this.criticalEmitted = true;
      this.criticalReached.emit(this.timeRemaining);
    }
  }

  private handleSessionExpired(): void {
    this.sessionExpired.emit();
  }

  getAriaLabel(): string {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;

    if (minutes > 0) {
      return `Session expires in ${minutes} minutes and ${seconds} seconds`;
    } else {
      return `Session expires in ${seconds} seconds`;
    }
  }
}
